import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

import type { IAccount } from '../accounts.types';

const ACCOUNT_API = {
  list: 'api/company/list',
  add: 'api/company',
  update: 'api/company',
  details: 'api/company/details',
  delete: 'api/company',
  import: 'api/company/import',
  export: 'api/company/export',
};

export const accountAPi = createApi({
  reducerPath: 'accountApi',
  baseQuery,
  endpoints: (builder) => ({
    // ----------------------------------------------------------------------
    // NOTE FOR DEVELOPERS:
    // Please maintain the following order for endpoints ensuring CRUD consistency:
    // 1. List (Filter)
    // 2. Details
    // 3. Create
    // 4. Update
    // 5. Delete
    // 6. Import
    // 7. Export
    // ----------------------------------------------------------------------

    filterAccount: builder.mutation({
      query: (filterQuery) => ({
        url: ACCOUNT_API.list,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { list?: IAccount[]; total?: number } }) => {
        const list = response?.data?.list ?? [];

        const customFieldObj = (obj: any) => {
          const cfObj = obj?.customFields?.reduce(
            (acc: any, cf: any) => {
              acc[cf.id] = cf.values;

              return acc;
            },
            {} as Record<string, any>
          );
          return cfObj || {};
        };

        const obj = {
          list: list?.map((item) => ({ ...item, customFields: customFieldObj(item) })) ?? [],
          total: response?.data?.total ?? 0,
        };

        return obj;
      },
    }),

    getAccountDtl: builder.mutation({
      query: (id: string) => ({
        url: `${ACCOUNT_API.details}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data: IAccount }) => ({
        data: { company: response.data },
      }),
    }),

    createAccount: builder.mutation<any, Partial<any>>({
      query: (newAccount) => ({
        url: ACCOUNT_API.add,
        method: 'POST',
        body: newAccount,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { company: IAccount };
        success: boolean;
      }): IAccount =>
        response.success && response.data?.company ? response.data.company : ({} as IAccount),
    }),

    updateAccount: builder.mutation<IAccount, { id: string; updates: Partial<IAccount> }>({
      query: ({ id, updates }) => ({
        url: `${ACCOUNT_API.update}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    deleteAccounts: builder.mutation({
      query: (ids: string[]) => ({
        url: ACCOUNT_API.delete,
        method: 'DELETE',
        body: { ids },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    importAccount: builder.mutation({
      query: (filePath) => ({
        url: ACCOUNT_API.import,
        method: 'POST',
        body: filePath,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    exportAccount: builder.mutation({
      query: (filter) => ({
        url: ACCOUNT_API.export,
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useFilterAccountMutation,
  useGetAccountDtlMutation,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useDeleteAccountsMutation,
  useImportAccountMutation,
  useExportAccountMutation,
} = accountAPi;
